import React, { useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import ExternalLinkIcon from "Components/Icons/ExternalLinkIcon";
import { DomainCompetency } from "Stores/courseStore";
import DebouncedInput from "Components/Common/DebouncedInput";
import { RecursiveAccordion } from "Components/Common/RecursiveAccordion";

interface DomainCompetenciesModalProps {
    value: string;
    competencies?: Array<DomainCompetency>;
    onSelect: (e: string) => void;
    isLoading: boolean;
}

export const DomainCompetenciesModal = ({
    value,
    competencies,
    onSelect,
    isLoading
}: DomainCompetenciesModalProps) => {
    const [openModal, setOpenModal] = useState(false);
    const [competencySelected, setCompetencySelected] = useState<string>(value ?? "");
    const [competencyId, setCompetencyId] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [data, setData] = useState(competencies);

    const onClose = () => {
        setOpenModal(false);
    };

    function filterCompetencies(data: DomainCompetency[], search: string): DomainCompetency[] {
        const filtered: DomainCompetency[] = [];
        for (const competency of data) {
            if (competency.standard_name.toLowerCase().includes(search.toLowerCase())) {
                filtered.push(competency);
            } else {
                const filteredChildren = filterCompetencies(competency.children, search);
                if (filteredChildren.length > 0) {
                    filtered.push({
                        ...competency,
                        children: filteredChildren
                    });
                }
            }
        }
        return filtered;
    }

    useEffect(() => {
        setData(filterCompetencies(competencies ?? [], searchValue));
    }, [competencies, searchValue]);

    const handleItemClick = (competency: DomainCompetency) => {
        setCompetencyId(competency.ruuid),
        setCompetencySelected(competency.standard_name);
    };

    return (
        <div className="select-custom-options">
            <Form.Group controlId="formClientID">
                <InputGroup 
                    className="input-group-new-label" 
                    onClick={() => {setOpenModal(true);}}
                >
                    <Form.Control
                        className="input-edit"
                        type="text"
                        value={competencySelected}
                        name="course-title"
                        placeholder={"Domain competency"}
                        readOnly
                    />
                    <InputGroup.Text className="addCompetency">
                        <ExternalLinkIcon
                            className={"addCompetencyExternalIcon"}
                            onClick={() => {
                                    setOpenModal(true);
                                }}
                        />
                    </InputGroup.Text>
                    <Form.Control.Feedback type="invalid">
                        Please select a competency.
                    </Form.Control.Feedback>
                </InputGroup>
            </Form.Group>
           <Modal
                className="styledCompetenciesModal"
                size="lg"
                show={openModal}
                onHide={onClose}
                scrollable={true}
            >
                <Modal.Header className="headerModal">
                    <i className={"icon icon-close"} onClick={onClose} />
                    <div className="headerContainer">
                        <div className="title-container">
                            <h1>
                                Select a competency
                            </h1>
                        </div>
                        <div className="search-bar">
                            <DebouncedInput
                                type="text"
                                value={searchValue}
                                onChange={(value: string) => {
                                    setSearchValue(value.toString());
                                }}
                                placeholder="Search by domain competency name"
                                className="search-input"
                            />
                            <i className={"type icon icon-search search-icon-bar"} aria-hidden="true" data-testid="icon-search"></i>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="bodyModal">
                    <RecursiveAccordion 
                        data={data ?? [] } 
                        onItemClick={handleItemClick} 
                        selectedId={competencyId} 
                    />
                </Modal.Body>
                <Modal.Footer className="footerModal">
                    <Button
                        className="confirmButton"
                        disabled={(!competencyId || isLoading) ?? false}
                        onClick={(e) => {
                            e.preventDefault();
                            onSelect(competencyId);
                            onClose();
                        }}
                        >
                        {isLoading && <Spinner size="sm" className="buttonSpinner"/>}
                        {isLoading ? "Loading..." : "Select"}
                    </Button>
                    <Button
                        onClick={() => {
                            onClose();
                            setCompetencySelected(value);
                            setCompetencyId("");
                        }}
                        className="cancelButton"
                    >
                            Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
